import React, { useState, useEffect, useRef } from 'react'
import { Search, Loader, MapPin, Star, ChevronDown, ChevronUp, AlertCircle, MessageSquare, Reply, Send, Link, CheckCircle, XCircle } from 'lucide-react'
import { apiClient, SmbFetchResponse, SmbReview } from '../services/api'

// ── Sentiment badge ───────────────────────────────────────────────────────────

function SentimentBadge({ sentiment }: { sentiment?: string | null }) {
  if (!sentiment) return <span className="text-gray-400 text-xs">—</span>
  const map: Record<string, { label: string; cls: string }> = {
    positive: { label: '😊 Positive', cls: 'bg-green-100 text-green-800' },
    negative: { label: '😞 Negative', cls: 'bg-red-100 text-red-800' },
    neutral:  { label: '😐 Neutral',  cls: 'bg-yellow-100 text-yellow-800' },
  }
  const cfg = map[sentiment.toLowerCase()] ?? { label: sentiment, cls: 'bg-gray-100 text-gray-700' }
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  )
}

// ── Star rating ───────────────────────────────────────────────────────────────

function Stars({ rating }: { rating: number }) {
  return (
    <span className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </span>
  )
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status?: string | null }) {
  if (!status) return null
  const map: Record<string, { label: string; cls: string }> = {
    posted:           { label: 'Ready to post',   cls: 'bg-green-100 text-green-800' },
    escalated:        { label: 'Escalated',        cls: 'bg-red-100 text-red-800' },
    already_replied:  { label: 'Already replied',  cls: 'bg-gray-100 text-gray-500' },
    skipped_no_text:  { label: 'No text',          cls: 'bg-gray-100 text-gray-500' },
    processing_failed:{ label: 'Failed',           cls: 'bg-orange-100 text-orange-700' },
    processed:        { label: 'Ready to post',    cls: 'bg-green-100 text-green-800' },
  }
  const cfg = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' }
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  )
}

// ── Expandable text cell (used for both SMB reply and AI response) ────────────

function TextCell({ text, emptyLabel = '—', labelColor = 'text-gray-700' }: {
  text?: string | null
  emptyLabel?: string
  labelColor?: string
}) {
  const [expanded, setExpanded] = useState(false)
  if (!text) return <span className="text-gray-400 text-xs italic">{emptyLabel}</span>

  const preview = text.length > 100 ? text.slice(0, 100) + '…' : text

  return (
    <div className={`text-sm ${labelColor}`}>
      <p>{expanded ? text : preview}</p>
      {text.length > 100 && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="mt-1 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
        >
          {expanded
            ? <><ChevronUp className="w-3 h-3" /> Show less</>
            : <><ChevronDown className="w-3 h-3" /> Show more</>}
        </button>
      )}
    </div>
  )
}

// ── Review table row ──────────────────────────────────────────────────────────

function ReviewRow({ review, index, placeId, testEmail, googleCredentials }: {
  review: SmbReview
  index: number
  placeId: string
  testEmail: string
  googleCredentials: string
}) {
  const isEscalated = review.should_escalate || review.status === 'escalated'
  const rowBg = isEscalated ? 'bg-red-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'

  const [posting, setPosting] = useState(false)
  const [postResult, setPostResult] = useState<'idle' | 'success' | 'error'>('idle')
  const [postError, setPostError] = useState('')

  // Email send state
  const [emailSending, setEmailSending] = useState(false)

  const [emailStatus, setEmailStatus] = useState<'idle' | 'sent' | 'failed' | 'approved' | 'rejected' | 'edited_approved'>('idle')

  const canPost = !!review.ai_response && !!review.review_id && !review.has_owner_reply && postResult === 'idle'
  const canEmail = !!review.ai_response && !!testEmail.trim() && emailStatus === 'idle'

  const handlePost = async () => {
    if (!review.ai_response || !review.review_id) return
    setPosting(true)
    setPostError('')
    try {
      await apiClient.postReply(review.review_id, review.ai_response, placeId, googleCredentials)
      setPostResult('success')
    } catch (err) {
      setPostResult('error')
      setPostError(err instanceof Error ? err.message : 'Failed to post reply')
    } finally {
      setPosting(false)
    }
  }

  const handleSendEmail = async () => {
    if (!review.ai_response || !testEmail.trim()) return
    setEmailSending(true)
    try {
      const result = await apiClient.sendSmbEmail({
        review_text: review.text || '',
        reviewer: review.reviewer,
        rating: review.rating,
        ai_response: review.ai_response,
        to_email: testEmail.trim(),
      })
      if (result.email_sent) {
        setEmailStatus('sent')
        // Poll for approval status every 3 seconds
        const interval = setInterval(async () => {
          try {
            const s = await apiClient.pollApprovalStatus(result.token)
            if (s.status !== 'pending') {
              setEmailStatus(s.status as typeof emailStatus)
              clearInterval(interval)
            }
          } catch {
            clearInterval(interval)
          }
        }, 3000)
      } else {
        setEmailStatus('failed')
      }
    } catch {
      setEmailStatus('failed')
    } finally {
      setEmailSending(false)
    }
  }

  const emailStatusBadge = () => {
    if (emailStatus === 'idle') return null
    const map: Record<string, { label: string; cls: string }> = {
      sent:           { label: '📧 Email sent — waiting…', cls: 'text-blue-600 bg-blue-50' },
      failed:         { label: '✗ Email failed',           cls: 'text-red-600 bg-red-50' },
      approved:       { label: '✓ Approved',               cls: 'text-green-700 bg-green-50' },
      rejected:       { label: '✗ Rejected',               cls: 'text-red-700 bg-red-50' },
      edited_approved:{ label: '✎ Edited & posted',        cls: 'text-purple-700 bg-purple-50' },
    }
    const cfg = map[emailStatus] ?? { label: emailStatus, cls: 'text-gray-600 bg-gray-50' }
    return (
      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
        {cfg.label}
      </span>
    )
  }

  return (
    <tr className={`${rowBg} border-b border-gray-100 align-top`}>
      {/* Date */}
      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap w-28">
        {review.date || '—'}
      </td>

      {/* Reviewer + rating */}
      <td className="px-4 py-3 w-36">
        <p className="text-sm font-medium text-gray-900 truncate">{review.reviewer}</p>
        <Stars rating={review.rating} />
      </td>

      {/* Review text */}
      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
        {review.text
          ? <p className="line-clamp-3">{review.text}</p>
          : <span className="text-gray-400 italic text-xs">Star-only rating</span>
        }
      </td>

      {/* Sentiment */}
      <td className="px-4 py-3 w-32">
        <SentimentBadge sentiment={review.sentiment} />
        {review.urgency && review.urgency !== 'low' && (
          <p className="text-xs text-gray-500 mt-1 capitalize">{review.urgency} urgency</p>
        )}
      </td>

      {/* SMB's existing reply */}
      <td className="px-4 py-3 max-w-xs">
        {review.has_owner_reply ? (
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Reply className="w-3 h-3 text-blue-500" />
              <span className="text-xs font-semibold text-blue-600">Owner replied</span>
              {review.owner_reply_date && (
                <span className="text-xs text-gray-400 ml-1">· {review.owner_reply_date}</span>
              )}
            </div>
            <TextCell text={review.owner_reply} emptyLabel="Reply exists (no text)" labelColor="text-blue-800" />
          </div>
        ) : (
          <span className="text-xs text-gray-400 italic">No reply yet</span>
        )}
      </td>

      {/* AI Response + Post It button */}
      <td className="px-4 py-3 max-w-sm">
        <TextCell text={review.ai_response} emptyLabel="—" />
        {review.validation_score != null && (
          <p className="text-xs text-gray-400 mt-1">Score: {review.validation_score}/100</p>
        )}

        {/* Post It button */}
        {review.ai_response && (
          <div className="mt-2 flex flex-col gap-1.5">
            {postResult === 'idle' && (
              <button
                onClick={handlePost}
                disabled={!canPost || posting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md
                           bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50
                           disabled:cursor-not-allowed transition-colors"
              >
                {posting
                  ? <><Loader className="w-3 h-3 animate-spin" /> Posting…</>
                  : <><Send className="w-3 h-3" /> Post It</>
                }
              </button>
            )}
            {postResult === 'success' && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-md">
                Posted to Google
              </span>
            )}
            {postResult === 'error' && (
              <div>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-1 rounded-md">
                  Failed to post
                </span>
                {postError && <p className="text-xs text-red-500 mt-1">{postError}</p>}
              </div>
            )}
            {review.has_owner_reply && postResult === 'idle' && (
              <p className="text-xs text-gray-400 mt-1">Already has a reply</p>
            )}

            {/* Send Email button */}
            <button
              onClick={handleSendEmail}
              disabled={!canEmail || emailSending}
              title={!testEmail.trim() ? 'Enter a test email address above first' : ''}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md
                         bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40
                         disabled:cursor-not-allowed transition-colors"
            >
              {emailSending
                ? <><Loader className="w-3 h-3 animate-spin" /> Sending…</>
                : <>📧 Send Email</>
              }
            </button>
            {emailStatusBadge()}
          </div>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3 w-32">
        <StatusBadge status={review.status} />
      </td>
    </tr>
  )
}

// ── Business info card ────────────────────────────────────────────────────────

function BusinessCard({ data }: { data: SmbFetchResponse }) {
  const s = data.summary
  return (
    <div className="card mb-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">{data.business_name}</h3>
          {data.address && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5" /> {data.address}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Stars rating={Math.round(data.overall_rating)} />
            <span className="text-sm font-semibold text-gray-700">{data.overall_rating}</span>
            <span className="text-sm text-gray-500">({data.total_reviews.toLocaleString()} reviews)</span>
          </div>
        </div>

        {/* Summary stats */}
        <div className="flex flex-wrap gap-3">
          <Stat label="Fetched" value={data.fetched_count} />
          <Stat label="AI responses" value={s.ai_responses_generated} color="text-primary-600" />
          <Stat label="Ready to post" value={s.would_auto_post} color="text-green-600" />
          <Stat label="Escalated" value={s.would_escalate} color="text-red-600" />
          <Stat label="Avg score" value={`${s.avg_validation_score}/100`} />
        </div>
      </div>

      {/* Sentiment breakdown */}
      {Object.keys(s.sentiment_breakdown).length > 0 && (
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
          {Object.entries(s.sentiment_breakdown).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <SentimentBadge sentiment={k} />
              <span className="text-sm font-semibold text-gray-700">{v}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-3 italic">{data.demo_note}</p>
    </div>
  )
}

function Stat({ label, value, color = 'text-gray-900' }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="text-center bg-gray-50 rounded-lg px-4 py-2 min-w-[72px]">
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SmbReviewTest() {
  const [businessName, setBusinessName] = useState('')
  const [location, setLocation] = useState('')
  const [brandVoice, setBrandVoice] = useState('friendly, professional, empathetic')
  const [testEmail, setTestEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [data, setData] = useState<SmbFetchResponse | null>(null)

  // Google OAuth state
  const [googleConnecting, setGoogleConnecting] = useState(false)
  const [googleConnected, setGoogleConnected] = useState(false)
  const [googleEmail, setGoogleEmail] = useState('')
  const [googleCredentials, setGoogleCredentials] = useState('')
  const [googleError, setGoogleError] = useState('')
  const popupRef = useRef<Window | null>(null)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return
      const { type, encrypted_credentials, email, error: oauthError } = event.data

      if (type === 'google_oauth_success') {
        setGoogleConnected(true)
        setGoogleEmail(email || '')
        setGoogleCredentials(encrypted_credentials || '')
        setGoogleError('')
        setGoogleConnecting(false)
        popupRef.current?.close()
      } else if (type === 'google_oauth_error') {
        setGoogleError(oauthError || 'Connection failed')
        setGoogleConnecting(false)
        popupRef.current?.close()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const handleConnectGoogle = async () => {
    setGoogleError('')
    setGoogleConnecting(true)
    try {
      const { url } = await apiClient.getGoogleOAuthUrl()
      const popup = window.open(url, 'google_oauth', 'width=520,height=640,scrollbars=yes')
      popupRef.current = popup

      // Detect if user closed popup without completing
      const timer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(timer)
          setGoogleConnecting(false)
        }
      }, 500)
    } catch (err) {
      setGoogleError(err instanceof Error ? err.message : 'Failed to start OAuth')
      setGoogleConnecting(false)
    }
  }

  const handleDisconnectGoogle = () => {
    setGoogleConnected(false)
    setGoogleEmail('')
    setGoogleCredentials('')
    setGoogleError('')
  }

  const handleFetch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!businessName.trim() || !location.trim()) return

    setError('')
    setIsLoading(true)
    setData(null)

    try {
      const result = await apiClient.fetchSmbReviews({
        business_name: businessName.trim(),
        location: location.trim(),
        limit: 2,
        brand_voice: brandVoice.trim() || undefined,
        process_reviews: true,
      })
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch reviews')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">

      {/* Search form */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-primary-600" />
          SMB Review Test
        </h2>

        {/* Google Business Profile Connect */}
        <div className="mb-5 p-4 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              {/* Google "G" icon */}
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div>
                <p className="text-sm font-semibold text-gray-800">Google Business Profile</p>
                {googleConnected
                  ? <p className="text-xs text-green-600">Connected as {googleEmail || 'Google Account'}</p>
                  : <p className="text-xs text-gray-500">Connect to read reviews and post responses</p>
                }
              </div>
            </div>

            {googleConnected ? (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle className="w-4 h-4" /> Connected
                </span>
                <button
                  type="button"
                  onClick={handleDisconnectGoogle}
                  className="text-xs text-gray-400 hover:text-red-500 underline transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleConnectGoogle}
                disabled={googleConnecting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              >
                {googleConnecting
                  ? <><Loader className="w-4 h-4 animate-spin" /> Connecting…</>
                  : <><Link className="w-4 h-4" /> Connect Google</>
                }
              </button>
            )}
          </div>

          {googleError && (
            <div className="mt-3 flex items-center gap-2 text-xs text-red-600">
              <XCircle className="w-4 h-4 flex-shrink-0" />
              <span>OAuth error: {googleError}. Check that <code>GOOGLE_CLIENT_ID</code> and <code>APP_BASE_URL</code> are set in your <code>.env</code>.</span>
            </div>
          )}
        </div>

        <form onSubmit={handleFetch} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={businessName}
                onChange={e => setBusinessName(e.target.value)}
                placeholder="e.g. Joe's Pizza"
                className="input"
                disabled={isLoading}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="e.g. Austin, TX"
                className="input"
                disabled={isLoading}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand Voice
              <span className="ml-1 text-xs font-normal text-gray-500">(optional — shapes the AI response tone)</span>
            </label>
            <input
              type="text"
              value={brandVoice}
              onChange={e => setBrandVoice(e.target.value)}
              placeholder="friendly, professional, empathetic"
              className="input"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Test Email Address
              <span className="ml-1 text-xs font-normal text-gray-500">(for "Send Email" buttons — local testing only)</span>
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="input"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !businessName.trim() || !location.trim()}
            className="btn btn-primary flex items-center gap-2"
          >
            {isLoading
              ? <><Loader className="w-4 h-4 animate-spin" /> Fetching &amp; Processing…</>
              : <><Search className="w-4 h-4" /> Fetch Last 2 Reviews</>
            }
          </button>
        </form>
      </div>

      {/* Results */}
      {data && (
        <>
          <BusinessCard data={data} />

          {/* Review table */}
          <div className="card p-0 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary-600" />
              <h3 className="font-semibold text-gray-900">
                Reviews &amp; AI Responses
              </h3>
              <span className="ml-auto text-xs text-gray-500">
                {data.reviews.length} reviews fetched
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Reviewer</th>
                    <th className="px-4 py-3">Review</th>
                    <th className="px-4 py-3">Sentiment</th>
                    <th className="px-4 py-3 text-blue-600">SMB Reply</th>
                    <th className="px-4 py-3 text-primary-600">AI Response</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reviews.map((review, i) => (
                    <ReviewRow key={i} review={review} index={i} placeId={data.place_id} testEmail={testEmail} googleCredentials={googleCredentials} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
